import React, { createContext, useState, useCallback, useEffect } from 'react';

import { useAuthContext } from './auth/AuthProvider';
import { getStoredData, setStoredData } from './sessionStorageHelpers';

const MFA_PHONE_SESSION_KEY = 'mfa_phone_number';
const PHONE_PREFIX = 'ph';
const MFA_EMAIL_SESSION_KEY = 'mfa_email_address';
const EMAIL_PREFIX = 'em';
const MFA_PHONE_ENROLLED_SESSION_KEY = 'mfa_phone_enrolled';
const MFA_EMAIL_ENROLLED_SESSION_KEY = 'mfa_email_enrolled';
const MFA_PASSKEY_ENROLLED_SESSION_KEY = 'mfa_passkey_enrolled';

// Helper functions for enrollment flags
const encodeEnrollmentFlag = (isEnrolled: boolean): string => {
  try {
    return btoa(`enr:${isEnrolled ? '1' : '0'}`);
  } catch {
    return isEnrolled ? '1' : '0';
  }
};

const decodeEnrollmentFlag = (storedValue: string | null): boolean => {
  if (!storedValue) {
    return false;
  }
  try {
    const decoded = atob(storedValue);
    if (decoded.startsWith('enr:')) {
      return decoded.slice(4) === '1';
    }
    return decoded === '1';
  } catch {
    return false;
  }
};

const getStoredEnrollmentFlag = (key: string): boolean => {
  try {
    const storedValue = sessionStorage.getItem(key);
    return decodeEnrollmentFlag(storedValue);
  } catch {
    return false;
  }
};

const setStoredEnrollmentFlag = (key: string, isEnrolled: boolean): void => {
  try {
    const encoded = encodeEnrollmentFlag(isEnrolled);
    sessionStorage.setItem(key, encoded);
  } catch (error) {
    console.warn(`Failed to store enrollment flag in sessionStorage for key ${key}:`, error);
  }
};

type Context = {
  setUserEmail: (email: string) => void;
  email: string;
  preferredFactor: string;
  setPreferredFactor: (factor: string) => void;
  mgmId: string;
  setUserMgmId: (userMgmId: string) => void;
  phoneNumber: string;
  setUserPhoneNumber: (phoneNumber: string) => void;
  hasPhoneEnrolled: boolean;
  setHasPhoneEnrolled: (hasPhoneEnrolled: boolean) => void;
  hasEmailEnrolled: boolean;
  setHasEmailEnrolled: (hasPhoneEnrolled: boolean) => void;
  hasPasskeyEnrolled: boolean;
  setHasPasskeyEnrolled: (hasPasskeyEnrolled: boolean) => void;
};

export const UserAccountContext = createContext<Context>({} as Context);

export const useUserAccount = () => {
  const context = React.useContext(UserAccountContext);

  if (context === undefined) {
    throw new Error('useUserAccount must be used within a UserAccountContextProvider');
  }

  return context;
};

export const UserAccountContextProvider = ({ children }: { children?: React.ReactNode }) => {
  const { authClient } = useAuthContext();
  const [email, setEmail] = useState<string>(() => getStoredData(MFA_EMAIL_SESSION_KEY, EMAIL_PREFIX));
  const [preferredFactor, setPreferredFactorState] = useState<string>('');
  const [mgmId, setMgmId] = useState<string>('');
  const [phoneNumber, setPhoneNumber] = useState<string>(() => getStoredData(MFA_PHONE_SESSION_KEY, PHONE_PREFIX));

  const [hasEmailEnrolled, setEmailEnrolled] = useState<boolean>(() =>
    getStoredEnrollmentFlag(MFA_EMAIL_ENROLLED_SESSION_KEY),
  );
  const [hasPhoneEnrolled, setPhoneEnrolled] = useState<boolean>(() =>
    getStoredEnrollmentFlag(MFA_PHONE_ENROLLED_SESSION_KEY),
  );
  const [hasPasskeyEnrolled, setPasskeyEnrolled] = useState<boolean>(() =>
    getStoredEnrollmentFlag(MFA_PASSKEY_ENROLLED_SESSION_KEY),
  );
  useEffect(() => {
    const fetchToken = async () => {
      const accessToken = await authClient.getAccessToken();
      if (!mgmId && accessToken?.claims?.uid) {
        setUserMgmId(String(accessToken.claims.uid));
      }

      if (!email && accessToken?.claims?.email) {
        setUserEmail(String(accessToken.claims.email));
      }
    };

    fetchToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setUserEmail = useCallback(userEmail => {
    setEmail(userEmail);
    setStoredData(MFA_EMAIL_SESSION_KEY, userEmail, EMAIL_PREFIX);
  }, []);

  const setPreferredFactor = useCallback((factor: string) => {
    setPreferredFactorState(factor);
  }, []);

  const setUserMgmId = useCallback((userMgmId: string) => {
    setMgmId(userMgmId);
  }, []);

  const setUserPhoneNumber = useCallback((userPhoneNumber: string) => {
    setPhoneNumber(userPhoneNumber);
    setStoredData(MFA_PHONE_SESSION_KEY, userPhoneNumber, PHONE_PREFIX);
  }, []);

  const setHasEmailEnrolled = useCallback(emailEnrolled => {
    setEmailEnrolled(emailEnrolled);
    setStoredEnrollmentFlag(MFA_EMAIL_ENROLLED_SESSION_KEY, emailEnrolled);
  }, []);

  const setHasPhoneEnrolled = useCallback(phoneEnrolled => {
    setPhoneEnrolled(phoneEnrolled);
    setStoredEnrollmentFlag(MFA_PHONE_ENROLLED_SESSION_KEY, phoneEnrolled);
  }, []);

  const setHasPasskeyEnrolled = useCallback(passkeyEnrolled => {
    setPasskeyEnrolled(passkeyEnrolled);
    setStoredEnrollmentFlag(MFA_PASSKEY_ENROLLED_SESSION_KEY, passkeyEnrolled);
  }, []);
  return (
    <UserAccountContext.Provider
      value={{
        email,
        setUserEmail,
        preferredFactor,
        setPreferredFactor,
        mgmId,
        setUserMgmId,
        phoneNumber,
        setUserPhoneNumber,
        hasEmailEnrolled,
        setHasEmailEnrolled,
        hasPhoneEnrolled,
        setHasPhoneEnrolled,
        hasPasskeyEnrolled,
        setHasPasskeyEnrolled,
      }}
    >
      {children}
    </UserAccountContext.Provider>
  );
};
